/*
 * ANANDA scroll engine — thin wrapper around GSAP + ScrollTrigger + Lenis.
 *
 * Loaded AFTER gsap.min.js + ScrollTrigger.min.js + lenis.min.js (deferred
 * load order in snippets/ananda-fx.liquid).
 *
 * Two responsibilities:
 *   1. Initialize Lenis smooth scrolling (on desktop, non-reduced-motion)
 *      and wire it into GSAP's ticker so ScrollTrigger updates fire in
 *      sync with Lenis's RAF loop. This is what makes scrub:* animations
 *      feel smooth instead of jerky from discrete mouse-wheel ticks.
 *   2. Expose a small AnandaScroll API (fadeUp / scrub / revealStagger)
 *      that custom sections call without repeating GSAP setup boilerplate.
 *
 * Design principles (Apple-clean):
 *   - Single shared easing: power3.out
 *   - Restrained entrance distance: ~30px translate, 0.9s duration
 *   - No bounce, no elastic, no overshoot
 *   - Respect prefers-reduced-motion: completely opt out, no animations,
 *     no Lenis either (let the OS handle scroll natively)
 *   - Skip Lenis on touch devices — mobile/tablet already have momentum
 *     scrolling and Lenis there can fight the OS feel.
 *   - Refresh on font-load so ScrollTrigger positions don't drift
 */
(function () {
  if (typeof gsap === 'undefined' || typeof ScrollTrigger === 'undefined') {
    console.warn('[ananda-scroll] GSAP not loaded — scroll API unavailable.');
    return;
  }

  gsap.registerPlugin(ScrollTrigger);

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // ---------- Lenis smooth scroll bootstrap ----------
  // Only enable on hover-capable (desktop-class) devices and when the
  // user hasn't requested reduced motion. Touch devices keep native
  // momentum scroll; Lenis there can feel sluggish or fight the OS.
  var lenis = null;
  if (typeof Lenis !== 'undefined' && !prefersReducedMotion) {
    var isTouch = window.matchMedia('(hover: none) and (pointer: coarse)').matches;
    if (!isTouch) {
      lenis = new Lenis({
        // duration in seconds for the smooth easing on each scroll input.
        // 1.2 is the Lenis default — noticeable inertia without feeling laggy.
        duration: 1.2,
        // Exponential out: starts fast, decelerates smoothly.
        easing: function (t) { return Math.min(1, 1.001 - Math.pow(2, -10 * t)); },
        smoothWheel: true,
      });

      // Drive ScrollTrigger updates from Lenis's scroll events so the
      // two stay perfectly in sync.
      lenis.on('scroll', ScrollTrigger.update);

      // Use GSAP's ticker as Lenis's RAF source (one shared frame loop
      // is cheaper and avoids ScrollTrigger races).
      gsap.ticker.add(function (time) {
        lenis.raf(time * 1000);
      });
      // GSAP's built-in lag smoothing fights Lenis — disable it.
      gsap.ticker.lagSmoothing(0);
    }
  }

  // Shared easing curves — keep all motion consistent across the site.
  var EASE = {
    smooth:     'cubic-bezier(0.16, 1, 0.3, 1)', // for CSS transitions
    gsapSmooth: 'power3.out',                     // GSAP equivalent
    scrub:      1.2,                              // smoothness for scrub-tied animations
  };

  /**
   * fadeUp — entrance reveal as the element enters the viewport.
   * Fades opacity 0→1, slides translateY 30px→0. Plays once, no reverse.
   */
  function fadeUp(target, opts) {
    if (prefersReducedMotion) return;
    opts = opts || {};
    var els = resolveTargets(target);
    if (!els.length) return;
    gsap.from(els, {
      opacity: 0,
      y: opts.distance != null ? opts.distance : 30,
      duration: opts.duration || 0.9,
      ease: EASE.gsapSmooth,
      stagger: opts.stagger || 0,
      scrollTrigger: {
        trigger: opts.trigger || els[0],
        start: opts.start || 'top 85%',
        toggleActions: 'play none none none',
      },
    });
  }

  /**
   * scrub — values tied directly to scroll progress, no enter/exit events.
   */
  function scrub(trigger, target, toVars, opts) {
    if (prefersReducedMotion) return;
    opts = opts || {};
    var els = resolveTargets(target);
    if (!els.length) return;
    gsap.to(els, Object.assign({}, toVars, {
      ease: 'none',
      scrollTrigger: {
        trigger: trigger,
        start: opts.start || 'top bottom',
        end:   opts.end   || 'bottom top',
        scrub: opts.scrub != null ? opts.scrub : EASE.scrub,
      },
    }));
  }

  /**
   * revealStagger — staggered fade-up for multiple children of a container.
   */
  function revealStagger(container, childSelector, opts) {
    if (prefersReducedMotion) return;
    opts = opts || {};
    var containerEl = typeof container === 'string'
      ? document.querySelector(container)
      : container;
    if (!containerEl) return;
    var children = containerEl.querySelectorAll(childSelector);
    if (!children.length) return;
    fadeUp(children, Object.assign({}, opts, {
      trigger: containerEl,
      stagger: opts.stagger != null ? opts.stagger : 0.08,
    }));
  }

  function resolveTargets(target) {
    if (!target) return [];
    if (typeof target === 'string') {
      return Array.prototype.slice.call(document.querySelectorAll(target));
    }
    if (target.nodeType === 1) return [target];
    if (target.length != null) {
      return Array.prototype.slice.call(target);
    }
    return [];
  }

  window.AnandaScroll = {
    fadeUp:        fadeUp,
    scrub:         scrub,
    revealStagger: revealStagger,
    EASE:          EASE,
    prefersReducedMotion: prefersReducedMotion,
    gsap:          gsap,
    ScrollTrigger: ScrollTrigger,
    lenis:         lenis, // null on touch / reduced-motion / no-Lenis
  };

  // Refresh ScrollTrigger after web fonts load and again after window
  // load — layout shifts move trigger boundaries.
  if (document.fonts && document.fonts.ready) {
    document.fonts.ready.then(function () { ScrollTrigger.refresh(); });
  }
  window.addEventListener('load', function () { ScrollTrigger.refresh(); });
})();
